// Normalizers for the authoritative upstream Live surfaces. Field names are read
// tolerantly (the upstream normalized shapes are consumed as-is and may evolve);
// anything genuinely absent stays null — never fabricated. fixture_id is the
// stable canonical identity and is preserved verbatim (no fuzzy matching).
import { LiveNowFixture, LiveCenterFixture, LiveEvent, LiveStatEntry } from "@/types/api";
import { fixtureKeys } from "@/presentation/fixtureIdentity";

function num(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function str(v: unknown): string | null {
  return v == null || v === "" ? null : String(v);
}
// A team field may be a plain string ("Manchester City W") OR an object ({name}).
// Resolve a real name from either; a non-empty string is a valid team name.
function stringOrName(v: unknown): string | undefined {
  if (typeof v === "string" && v.trim()) return v.trim();
  if (v && typeof v === "object" && typeof (v as any).name === "string" && (v as any).name.trim()) {
    return (v as any).name.trim();
  }
  return undefined;
}
function pick(o: any, keys: string[]): any {
  if (!o || typeof o !== "object") return undefined;
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k];
  return undefined;
}

// Robustly locate the array of fixture rows anywhere in the upstream envelope.
// Handles {matches}, {rows}, {data:{matches}}, bare arrays, etc. Falls back to a
// recursive search for the first array whose items carry a fixture identity.
function findFixtureArray(raw: any, depth = 0): any[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object" || depth > 4) return [];
  for (const k of ["matches", "rows", "fixtures", "items", "data", "live", "live_now", "liveNow", "result", "response", "results"]) {
    if (Array.isArray(raw[k])) return raw[k];
  }
  for (const k of Object.keys(raw)) {
    const v = raw[k];
    if (Array.isArray(v) && v.some((e) => e && typeof e === "object" && (e.fixture_id ?? e.fixtureId ?? e.id) != null)) return v;
    if (v && typeof v === "object") {
      const nested = findFixtureArray(v, depth + 1);
      if (nested.length) return nested;
    }
  }
  return [];
}

// Upstream /atlas/data/live?live=all → list of normalized live fixtures.
export function normalizeLiveNow(raw: any): LiveNowFixture[] {
  const list = findFixtureArray(raw);
  if (!Array.isArray(list)) return [];
  const out: LiveNowFixture[] = [];
  for (const it of list) {
    const fid = str(pick(it, ["fixture_id", "fixtureId", "id"]));
    if (!fid) continue; // no canonical identity → skip (no fuzzy matching)
    const sc = it && typeof it.score === "object" && it.score ? it.score : null;
    const tms = (it && typeof it.teams === "object" && it.teams) || null;
    out.push({
      fixture_id: fid,
      keys: fixtureKeys(it),
      country: str(pick(it, ["country"])),
      league: str(pick(it, ["league", "competition"])),
      home_team: str(pick(it, ["home_team", "homeTeam"]) ?? stringOrName(it?.home) ?? stringOrName(tms?.home) ?? stringOrName(it?.fixture?.teams?.home)) ?? "",
      away_team: str(pick(it, ["away_team", "awayTeam"]) ?? stringOrName(it?.away) ?? stringOrName(tms?.away) ?? stringOrName(it?.fixture?.teams?.away)) ?? "",
      home_goals: num(it?.live_score_home ?? sc?.home ?? pick(it, ["home_goals", "homeGoals", "goals_home", "score_home"])),
      away_goals: num(it?.live_score_away ?? sc?.away ?? pick(it, ["away_goals", "awayGoals", "goals_away", "score_away"])),
      minute: num(it?.live_minute ?? pick(it, ["minute", "elapsed", "clock"])),
      status: str(it?.live_status ?? pick(it, ["status", "status_short", "state", "phase"])),
      updated_at: str(pick(it, ["updated_at", "updatedAt", "last_update", "generated_at"])),
      home_logo: str(pick(it, ["home_logo", "homeLogo", "home_team_logo"]) ?? it?.home?.logo ?? tms?.home?.logo),
      away_logo: str(pick(it, ["away_logo", "awayLogo", "away_team_logo"]) ?? it?.away?.logo ?? tms?.away?.logo),
      league_logo: str(pick(it, ["league_logo", "leagueLogo", "competition_logo"]) ?? it?.league?.logo),
      home_team_id: pick(it, ["home_team_id", "homeTeamId"]) ?? tms?.home?.id ?? null,
      away_team_id: pick(it, ["away_team_id", "awayTeamId"]) ?? tms?.away?.id ?? null,
      league_id: pick(it, ["league_id", "leagueId"]) ?? null,
    });
  }
  return out;
}

// Upstream /atlas/data/live-center/{fixture_id} → normalized fixture detail.
export function normalizeLiveCenter(raw: any): LiveCenterFixture | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw.fixture ?? raw.data ?? raw;
  const events: LiveEvent[] = Array.isArray(d?.events ?? d?.timeline)
    ? (d.events ?? d.timeline).map((e: any) => ({
        minute: num(pick(e, ["minute", "elapsed", "time"])),
        type: str(pick(e, ["type", "event", "kind"])),
        detail: str(pick(e, ["detail", "description", "text"])),
        team: str(pick(e, ["team", "team_name"])),
        player: str(pick(e, ["player", "player_name"])),
      }))
    : [];
  let statistics: LiveStatEntry[] = [];
  const rawStats = d?.statistics ?? d?.stats;
  if (Array.isArray(rawStats)) {
    statistics = rawStats.map((s: any) => ({
      key: str(pick(s, ["key", "type", "code"])) ?? "",
      label: str(pick(s, ["label", "name", "type"])),
      home: pick(s, ["home", "home_value"]) ?? null,
      away: pick(s, ["away", "away_value"]) ?? null,
    })).filter((s: LiveStatEntry) => s.key);
  } else if (rawStats && typeof rawStats === "object") {
    statistics = Object.entries(rawStats).map(([key, v]: [string, any]) => ({
      key, label: key, home: v?.home ?? null, away: v?.away ?? null,
    }));
  }
  const res = d?.resolution;
  const sc = d && typeof d.score === "object" && d.score ? d.score : null;
  return {
    fixture_id: str(pick(d, ["fixture_id", "fixtureId", "id"])) ?? undefined,
    country: str(pick(d, ["country"])),
    league: str(pick(d, ["league", "competition"])),
    home_team: str(pick(d, ["home_team", "homeTeam", "home"])),
    away_team: str(pick(d, ["away_team", "awayTeam", "away"])),
    home_team_id: pick(d, ["home_team_id", "homeTeamId"]) ?? null,
    away_team_id: pick(d, ["away_team_id", "awayTeamId"]) ?? null,
    home_logo: str(pick(d, ["home_logo", "homeLogo", "home_team_logo"])),
    away_logo: str(pick(d, ["away_logo", "awayLogo", "away_team_logo"])),
    league_logo: str(pick(d, ["league_logo", "leagueLogo", "competition_logo"])),
    league_id: pick(d, ["league_id", "leagueId"]) ?? null,
    home_goals: num(sc?.home ?? pick(d, ["home_goals", "homeGoals", "score_home"])),
    away_goals: num(sc?.away ?? pick(d, ["away_goals", "awayGoals", "score_away"])),
    minute: num(pick(d, ["minute", "elapsed", "clock"])),
    status: str(pick(d, ["status", "status_short", "state"])),
    lifecycle_state: str(pick(d, ["lifecycle_state", "lifecycle", "phase"])),
    updated_at: str(pick(d, ["updated_at", "updatedAt", "last_update", "generated_at"])),
    stale: typeof d?.stale === "boolean" ? d.stale : null,
    events,
    statistics,
    resolution: res ? {
      result: str(pick(res, ["result", "outcome"])),
      final_home_goals: num(pick(res, ["final_home_goals", "home"])),
      final_away_goals: num(pick(res, ["final_away_goals", "away"])),
    } : null,
  };
}
